/**
 * ➕ SET AIRPORT PRICING DIALOG
 * Dialog for setting airport transfer pricing for a vehicle
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Stack,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Flight, Room } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { airportPricingService, VehicleWithPricing, SetPricingData } from '@/services/airportPricingService';
import { Airport } from '@/services/airportService';
import { airportZoneService, AirportZone } from '@/services/airportZoneService';

interface SetAirportPricingDialogProps {
  open: boolean;
  vehicle: VehicleWithPricing;
  airports: Airport[];
  onClose: () => void;
  onSuccess: () => void;
}

export const SetAirportPricingDialog: React.FC<SetAirportPricingDialogProps> = ({
  open,
  vehicle,
  airports,
  onClose,
  onSuccess,
}) => {
  const [selectedAirport, setSelectedAirport] = useState('');
  const [zones, setZones] = useState<AirportZone[]>([]);
  const [selectedZone, setSelectedZone] = useState('');
  const [pickupPrice, setPickupPrice] = useState<number>(0);
  const [dropoffPrice, setDropoffPrice] = useState<number>(0);
  const [isAvailable, setIsAvailable] = useState(true);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);

  useEffect(() => {
    if (selectedAirport) {
      loadZones();
    } else {
      setZones([]);
      setSelectedZone('');
    }
  }, [selectedAirport]);

  const loadZones = async () => {
    setLoadingZones(true);
    try {
      const response = await airportZoneService.getZones(selectedAirport);
      if (response.success && response.zones) {
        setZones(response.zones.filter(z => z.isActive));
      } else {
        toast.error('Failed to load zones');
      }
    } catch (error) {
      console.error('Error loading zones:', error);
      toast.error('Failed to load zones');
    } finally {
      setLoadingZones(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedZone) {
      toast.error('Please select a zone');
      return;
    }

    if (pickupPrice <= 0 && dropoffPrice <= 0) {
      toast.error('Please set at least one price');
      return;
    }

    setLoading(true);
    try {
      const data: SetPricingData = {
        zoneId: selectedZone,
        pickupPrice: Math.round(pickupPrice * 100), // Convert to kobo
        dropoffPrice: Math.round(dropoffPrice * 100), // Convert to kobo
        isAvailable,
        notes: notes.trim() || undefined,
      };

      const response = await airportPricingService.setPricing(vehicle.vehicleId, data);
      if (response.success) {
        toast.success('Airport pricing set successfully!');
        onSuccess();
      } else {
        toast.error(response.error || 'Failed to set pricing');
      }
    } catch (error) {
      console.error('Error setting pricing:', error);
      toast.error('Failed to set pricing');
    } finally {
      setLoading(false);
    }
  };

  const selectedAirportData = airports.find(a => a.id === selectedAirport);
  const selectedZoneData = zones.find(z => z.id === selectedZone);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Set Airport Transfer Pricing
      </DialogTitle>
      <DialogContent>
        {/* Vehicle Info */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            {vehicle.vehicleMake} {vehicle.vehicleModel} ({vehicle.vehicleYear})
          </Typography>
          <Typography variant="caption">
            Plate: {vehicle.plateNumber}
          </Typography>
        </Alert>

        {/* Airport Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Airport *</InputLabel>
          <Select
            value={selectedAirport}
            label="Select Airport *"
            onChange={(e) => setSelectedAirport(e.target.value)}
          >
            {airports.filter(a => a.isActive).map((airport) => (
              <MenuItem key={airport.id} value={airport.id}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Flight fontSize="small" />
                  <span>{airport.airportCode} - {airport.airportName}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Zone Selection */}
        <FormControl fullWidth sx={{ mb: 2 }} disabled={!selectedAirport || loadingZones}>
          <InputLabel>Select Zone *</InputLabel>
          <Select
            value={selectedZone}
            label="Select Zone *"
            onChange={(e) => setSelectedZone(e.target.value)}
          >
            {loadingZones ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading zones...
              </MenuItem>
            ) : zones.length === 0 ? (
              <MenuItem disabled>No zones available</MenuItem>
            ) : (
              zones.map((zone) => (
                <MenuItem key={zone.id} value={zone.id}>
                  <Stack>
                    <Typography variant="body2" fontWeight={500}>
                      {zone.zoneName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {zone.distanceRange.display} • {zone.zoneDescription}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Selected Zone Info */}
        {selectedZoneData && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Room fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={600}>
                {selectedZoneData.zoneName}
              </Typography>
              <Chip label={selectedZoneData.distanceRange.display} size="small" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {selectedZoneData.zoneDescription}
            </Typography>
            {selectedZoneData.landmarks && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Landmarks: {selectedZoneData.landmarks}
              </Typography>
            )}
          </Box>
        )}

        {/* Pricing Fields */}
        <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
          Pricing (in Naira)
        </Typography>

        <TextField
          fullWidth
          label="Pickup Price"
          type="number"
          value={pickupPrice || ''}
          onChange={(e) => setPickupPrice(parseFloat(e.target.value) || 0)}
          InputProps={{
            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
          }}
          sx={{ mb: 2 }}
          helperText="Price for pickup from this zone to airport"
        />

        <TextField
          fullWidth
          label="Dropoff Price"
          type="number"
          value={dropoffPrice || ''}
          onChange={(e) => setDropoffPrice(parseFloat(e.target.value) || 0)}
          InputProps={{
            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
          }}
          sx={{ mb: 2 }}
          helperText="Price for dropoff from airport to this zone"
        />

        {/* Availability Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={isAvailable}
              onChange={(e) => setIsAvailable(e.target.checked)}
            />
          }
          label="Available for booking"
          sx={{ mb: 2 }}
        />

        {/* Notes */}
        <TextField
          fullWidth
          label="Notes (Optional)"
          multiline
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any internal notes about this pricing..."
          helperText="These notes are only visible to admins"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedZone || (pickupPrice <= 0 && dropoffPrice <= 0)}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Setting...' : 'Set Pricing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetAirportPricingDialog;
